import {
  useEffect,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
  type SVGProps,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import {
  ArrowUpRight,
  Bookmark,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronUp,
  CornerUpRight,
  Globe,
  Image as ImageIcon,
  ImagePlus,
  Link2Off,
  LoaderCircle,
  MoreHorizontal,
  Pencil,
  Ratio,
  Sparkles,
  VideoOff,
  Orbit,
  PenLine,
  Play,
  Plus,
  Radio,
  RefreshCw,
  TrendingUp,
  X,
} from "lucide-react";
import { Image } from "@/components/Image";
import { MediaCarousel } from "@/components/MediaCarousel";
import { Button } from "@/components/form/Button";
import { cn } from "@/utils/helpers";
import { kitBareFieldClass } from "@/app/routes/dashboard/components/kit/Field";
import {
  MEDIA_TYPE_LABEL,
  displayAspectRatio,
  mediaTypeHasDuration,
  type CalendarPlanSlot,
  type CalendarPlatform,
  type CalendarSlotIdea,
  type IdeaReference,
  type IdeaSource,
} from "@/types/contentCalendar.type";
import PlatformGlyph, {
  hasGlyph,
} from "@/app/routes/dashboard/components/PlatformGlyph";
import { KitModal } from "../../components/kit/Modal";
import { KitMenu, KitMenuItem, KitMenuPopover, KitMenuTrigger, KitPressable } from "../../components/kit/Menu";
import { useCoShellPortal } from "../../CoShellPortalContext";
import { formatDuration } from "../calendar.utils";
import { PLATFORM_META, PlatformPicker } from "../components/PlatformPicker";
import ElapsedSince from "../ElapsedSince";
import {
  adaptationNote,
  evidenceKindLabel,
  evidenceTarget,
  foldHooks,
  ideaSourceLine,
  referenceGone,
  referenceKindLabel,
  specsLine,
} from "./ideaCardModel";
import "../components/postPage.css";
import "./ideas.css";

export interface EditorialConceptActions {
  /** Picks an idea. The post page's header calls it; the cards only select. */
  onAccept: (ideaId: string) => void;
  onRegenerate: (ideaId: string) => void;
  onModify: (ideaId: string, instruction: string) => void;
  /**
   * Redirect in the chat: pins the idea to the composer ("Replying to Idea 2").
   * Returns false when no chat is there to take it; the card then asks inline.
   */
  onRedirect?: (ideaId: string) => boolean;
  onEditIdea: (
    ideaId: string,
    fields: { channels?: CalendarPlatform[] },
  ) => void;
  /** Opens the manual editor for the idea's words (the card's more menu). */
  onEditWords?: (ideaId: string) => void;
  onChangeMedia: (ideaId: string) => void;
  onRepitchAll: () => void;
  /** Write your own idea: replaces one of the three with the agent's write-up of your words. */
  onWriteOwn?: () => void;
  onCancelRework?: (ideaId: string) => void;
}

type Viewport = "desktop" | "tablet" | "phone";
/** What started an idea's rewrite: a chat direction, New angle, or your own words. */
export type IdeaReworkSource = "chat" | "angle" | "own";
type Icon = ComponentType<SVGProps<SVGSVGElement> & { strokeWidth?: number }>;

const EVIDENCE_ICON: Record<string, Icon> = {
  trend: TrendingUp,
  signal: Radio,
  universe: Orbit,
  web: Globe,
  media: ImageIcon,
  reference: Bookmark,
};

/** Section titles (IdeaCard): Barlow Condensed 700 13.5, tracked 0.8, capitals, muted. */
const SECTION =
  "font-section text-[13.5px] font-bold uppercase leading-[16px] tracking-[0.8px] text-co-muted";
/** A label row: the section title, then its count pushed right (Manrope 12, muted). */
const SECTION_ROW = "flex items-center justify-between gap-3";
const SECTION_COUNT = "text-[12px] leading-4 text-co-muted";
const TEXT_BUTTON =
  "inline-flex h-8 items-center gap-1.5 text-[13px] font-medium text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:cursor-not-allowed disabled:opacity-40";

function clientSees(clientName: string | null | undefined): string {
  const name = clientName?.trim();
  return `${name || "The client"} sees this one`;
}

// ------------------------------------------------------------------ media

/** "9:16" as a CSS aspect ratio; null for anything that is not a ratio. */
function cssRatio(type: string | null | undefined): string | null {
  const match = /^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/.exec(type?.trim() ?? "");
  return match ? `${match[1]} / ${match[2]}` : null;
}

/**
 * The reference inside the stage (Idea media · Vertical clip): the clip at its
 * own ratio, full stage height, centred on the recess with radius 12. Never
 * cropped: the clip box takes the frame's ratio. A carousel fills the stage.
 */
function ReferenceMedia({ reference, fill = false }: { reference: IdeaReference; fill?: boolean }) {
  const [playing, setPlaying] = useState(false);
  const mediaType =
    reference.mediaType ?? (reference.imageUrl ? "image" : "video");
  const imageUrl = reference.imageUrl ?? reference.thumbnailUrl;
  // A pasted carousel pages through every slide instead of showing only its cover.
  const slides = reference.items ?? [];
  const ratio = fill ? null : cssRatio(reference.type);

  if (slides.length > 1) return <MediaCarousel items={slides} label={reference.label} />;

  const media =
    playing && reference.videoUrl ? (
      <video
        src={reference.videoUrl}
        poster={reference.thumbnailUrl ?? undefined}
        controls
        autoPlay
        playsInline
        className="size-full object-cover"
      />
    ) : mediaType === "image" && imageUrl ? (
      <Image src={imageUrl} alt={reference.label} className="size-full object-cover" />
    ) : reference.thumbnailUrl ? (
      <Image src={reference.thumbnailUrl} alt={reference.label} className="size-full object-cover" />
    ) : reference.videoUrl ? (
      <video src={reference.videoUrl} muted playsInline preload="metadata" className="size-full object-cover" />
    ) : null;

  return (
    <div
      style={ratio ? { aspectRatio: ratio } : undefined}
      className={cn("relative overflow-hidden", ratio ? "h-full max-w-full rounded-card" : "size-full")}
    >
      {media}
      {mediaType === "video" && reference.videoUrl && !playing ? (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          aria-label={`Play ${reference.label}`}
          className="co-media-chip absolute left-1/2 top-1/2 !size-11 -translate-x-1/2 -translate-y-1/2 !rounded-pill outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
        >
          <Play className="size-[18px] translate-x-px" fill="currentColor" strokeWidth={0} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  );
}

/**
 * The media stage (Idea media): 214 tall on the recess with the card's top
 * corners, the reference at its own ratio, media-glass chips on its corners.
 * Without media it offers Add media; a reference whose file is gone (the read
 * returns no URL at all) says so and offers Pick another.
 */
function IdeaMediaStage({
  idea,
  outputRatio,
  selected,
  onChangeMedia,
  className,
  atRatio = false,
}: {
  idea: CalendarSlotIdea;
  outputRatio: string;
  selected?: boolean;
  onChangeMedia?: () => void;
  className?: string;
  /** The phone: the stage takes the reference's own ratio at full width. */
  atRatio?: boolean;
}) {
  const reference = idea.reference;
  const gone = referenceGone(reference);
  const adapt =
    reference && !gone ? adaptationNote(reference.type, outputRatio) : null;
  const platform =
    reference?.platform && hasGlyph(reference.platform.toUpperCase())
      ? reference.platform.toUpperCase()
      : null;

  return (
    <div
      style={
        atRatio && reference && !gone
          ? { aspectRatio: reference.type.replace(":", " / ") }
          : undefined
      }
      className={cn(
        "@container relative flex h-[214px] shrink-0 items-center justify-center overflow-hidden rounded-t-card bg-co-recess shadow-[inset_0_-1px_0_var(--co-line)]",
        className,
      )}
    >
      {reference && !gone ? (
        <>
          <ReferenceMedia reference={reference} fill={atRatio} />
          <span className="absolute left-2.5 top-2.5 flex max-w-[calc(100%-56px)] items-center gap-1.5">
            <span className="co-media-chip">
              {platform ? (
                <PlatformGlyph platform={platform} className="size-3.5" />
              ) : (
                <ImageIcon className="size-[13px]" strokeWidth={1.8} aria-hidden="true" />
              )}
            </span>
            <span className="co-media-chip co-media-chip--label min-w-0 !font-medium">
              <span className="hidden truncate @min-[300px]:inline">
                {referenceKindLabel(reference)} · shot {reference.type}
              </span>
              <span className="sr-only @min-[300px]:hidden">
                {referenceKindLabel(reference)}, shot {reference.type}
              </span>
            </span>
          </span>
          <span className="absolute inset-x-2.5 bottom-3 flex items-center gap-1.5">
            {adapt ? (
              <span className="co-media-chip co-media-chip--label min-w-0 !font-medium" title={adapt}>
                <Ratio className="size-[13px] shrink-0" strokeWidth={1.8} aria-hidden="true" />
                {/* Beside Change media it needs the room; narrower, the icon and its title carry it. */}
                <span className="hidden truncate @min-[300px]:inline">{adapt}</span>
                <span className="sr-only @min-[300px]:hidden">{adapt}</span>
              </span>
            ) : null}
            <span className="flex-1" />
            {onChangeMedia ? (
              <button
                type="button"
                onClick={onChangeMedia}
                className="co-media-chip co-media-chip--label !font-medium outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                <ImagePlus className="size-[13px]" strokeWidth={1.8} aria-hidden="true" />
                <span className="hidden @min-[300px]:inline">Change media</span>
                <span className="sr-only @min-[300px]:hidden">Change media</span>
              </button>
            ) : null}
          </span>
        </>
      ) : gone ? (
        // Idea media · Unavailable. Only what the read says: the file is gone.
        <div className="flex max-w-[300px] flex-col items-center gap-2 px-5 text-center">
          <VideoOff className="size-[22px] text-co-muted" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-[12.5px] font-semibold text-co-text">
            This {reference?.mediaType === "image" ? "picture" : "clip"} is no longer available.
          </p>
          <p className="text-[12.5px] leading-[1.45] text-co-muted">
            The idea still stands. Pick another reference, or keep it without one.
          </p>
          {onChangeMedia ? (
            <Button
              size="small"
              variant="secondary"
              buttonStyle="outline"
              icon={ImagePlus}
              iconPosition="right"
              iconClassName="size-3.5"
              onPress={onChangeMedia}
            >
              Pick another
            </Button>
          ) : null}
        </div>
      ) : (
        <div className="flex max-w-[340px] flex-col items-center gap-2 px-5 text-center">
          <ImagePlus className="size-[22px] text-co-muted" strokeWidth={1.5} aria-hidden="true" />
          <p className="text-[12.5px] leading-snug text-co-muted">
            No reference yet. The idea stands on its words.
          </p>
          {onChangeMedia ? (
            <Button
              size="small"
              variant="secondary"
              buttonStyle="outline"
              icon={Plus}
              iconPosition="right"
              iconClassName="size-3.5"
              onPress={onChangeMedia}
            >
              Add media
            </Button>
          ) : null}
        </div>
      )}

      {selected ? (
        <span
          className="absolute right-2.5 top-2.5 grid size-[26px] place-items-center rounded-[7px] bg-co-accent-fill text-co-on-accent"
          aria-hidden="true"
        >
          <Check className="size-[15px]" strokeWidth={2.2} />
        </span>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------------- sections

/** The agent's recommendation, in the accent under the title. Hidden until backend #124 adds a field for it. */
function RecommendationLine({ text }: { text: string | null }) {
  if (!text) return null;
  return (
    <p className="flex gap-1.5 text-[12px] leading-[1.4] text-co-accent-ink">
      <Sparkles className="mt-px size-[13px] shrink-0 text-co-accent" strokeWidth={1.8} aria-hidden="true" />
      {text}
    </p>
  );
}

/** One evidence row: a radius 4 well on the recess, the kind in the accent. */
const EVIDENCE_ROW =
  "flex items-center gap-2 rounded-[4px] bg-co-recess px-2.5 py-[9px] text-[12.5px] leading-[1.4]";

function EvidenceRow({ source }: { source: IdeaSource }) {
  const Glyph = EVIDENCE_ICON[source.type] ?? Globe;
  const target = evidenceTarget(source);
  const content = (
    <>
      <Glyph className="size-3.5 shrink-0 text-co-accent" strokeWidth={1.8} aria-hidden="true" />
      <span className="shrink-0 text-co-accent-ink">{evidenceKindLabel(source.type)}</span>
      <span className="min-w-0 flex-1 truncate text-co-text2">{source.label}</span>
      {target ? (
        <ArrowUpRight className="size-3.5 shrink-0 text-co-muted" strokeWidth={1.8} aria-hidden="true" />
      ) : null}
    </>
  );
  const linked =
    "outline-none transition-colors hover:bg-co-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent";
  if (target?.kind === "external") {
    return (
      <a href={target.href} target="_blank" rel="noreferrer" className={cn(EVIDENCE_ROW, linked)}>
        {content}
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    );
  }
  if (target?.kind === "internal") {
    return (
      <Link to={target.to} className={cn(EVIDENCE_ROW, linked)}>
        {content}
      </Link>
    );
  }
  return <div className={EVIDENCE_ROW}>{content}</div>;
}

function EvidenceSection({ sources }: { sources: IdeaSource[] }) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className={SECTION_ROW}>
        <p className={SECTION}>Evidence</p>
        {sources.length ? <span className={SECTION_COUNT}>{sources.length}</span> : null}
      </div>
      {sources.length ? (
        sources.map((source, index) => <EvidenceRow key={`${source.type}-${index}`} source={source} />)
      ) : (
        <div className={cn(EVIDENCE_ROW, "text-co-muted")}>
          <Link2Off className="size-3.5 shrink-0" strokeWidth={1.8} aria-hidden="true" />
          No sources linked to this idea
        </div>
      )}
    </div>
  );
}

function HookLine({
  hook,
  mark,
  clientName,
}: {
  hook: string;
  mark: boolean;
  clientName?: string | null;
}) {
  return (
    <li className="flex gap-2.5">
      <span
        aria-hidden="true"
        className={cn(
          "mt-0.5 grid size-4 shrink-0 place-items-center rounded-full border-[1.5px]",
          mark ? "border-co-accent-fill" : "border-co-line2",
        )}
      >
        {mark ? <span className="size-1.5 rounded-full bg-co-accent-fill" /> : null}
      </span>
      <span className="flex min-w-0 flex-col gap-1">
        <span className="text-[13px] leading-[1.45] text-co-text">“{hook}”</span>
        {mark ? <span className="text-[11.5px] leading-4 text-co-accent-ink">{clientSees(clientName)}</span> : null}
      </span>
    </li>
  );
}

/**
 * Suggested hooks. The client only ever sees hooks[0], so that one carries the
 * mark; the fold shows it and "2 more hooks" opens the rest.
 */
function HooksSection({
  hooks,
  clientName,
  alwaysOpen = false,
}: {
  hooks: string[];
  clientName?: string | null;
  alwaysOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const fold = foldHooks(hooks, open || alwaysOpen);
  const total = hooks.filter((hook) => hook.trim()).length;
  if (total === 0) return null;
  return (
    <div className="flex flex-col gap-2.5">
      <div className={SECTION_ROW}>
        <p className={SECTION}>Suggested hooks</p>
        <span className={SECTION_COUNT}>{total}</span>
      </div>
      <ul className="flex flex-col gap-2.5">
        {fold.shown.map((hook, index) => (
          <HookLine key={`${index}-${hook}`} hook={hook} mark={index === 0} clientName={clientName} />
        ))}
      </ul>
      {alwaysOpen ? null : fold.moreLabel || open ? (
        <button
          type="button"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
          className={cn(TEXT_BUTTON, "-my-1.5 self-start pl-[26px] pt-0.5")}
        >
          {open ? "Fewer hooks" : fold.moreLabel}
          {open ? (
            <ChevronUp className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          ) : (
            <ChevronDown className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
          )}
        </button>
      ) : null}
    </div>
  );
}

/**
 * Channels: the label, the idea's channel marks (14 in muted, 28 square each)
 * and Add channel on the control glass, which opens the picker under them.
 */
function ChannelsSection({
  idea,
  editable,
  onChange,
}: {
  idea: CalendarSlotIdea;
  editable: boolean;
  onChange?: (channels: CalendarPlatform[]) => void;
}) {
  const [editing, setEditing] = useState(false);
  return (
    <div>
      {/* Wraps at narrow widths, so the marks never run under the foot's rule. */}
      <div className="flex min-h-7 flex-wrap items-center gap-1.5">
        <p className={cn(SECTION, "mr-auto")}>Channels</p>
        {idea.channels.map((channel) => (
          <span key={channel} className="grid size-7 place-items-center text-co-muted">
            <PlatformGlyph platform={channel} className="size-3.5" />
            <span className="sr-only">{PLATFORM_META[channel]?.label ?? channel}</span>
          </span>
        ))}
        {editable && onChange ? (
          <button
            type="button"
            aria-expanded={editing}
            onClick={() => setEditing((value) => !value)}
            className="co-glass-control inline-flex h-7 items-center gap-[5px] rounded-pill px-2.5 text-[12.5px] font-medium text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent"
          >
            {editing ? (
              <Check className="size-3.5" strokeWidth={2} aria-hidden="true" />
            ) : (
              <Plus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            )}
            {editing ? "Done" : "Add channel"}
          </button>
        ) : null}
      </div>
      {editing && onChange ? (
        <PlatformPicker className="mt-3" value={idea.channels} onChange={onChange} />
      ) : null}
    </div>
  );
}

/** Redirect when no chat is there to take it: the direction is typed on the card. */
function InlineRedirect({
  onSubmit,
  onCancel,
}: {
  onSubmit: (instruction: string) => void;
  onCancel: () => void;
}) {
  const [instruction, setInstruction] = useState("");
  const submit = () => {
    const value = instruction.trim();
    if (value) onSubmit(value);
  };
  return (
    <div className="border-t border-co-line px-[18px] py-4">
      <label className="text-[12.5px] text-co-muted" htmlFor="idea-redirect">
        Where should this idea go?
      </label>
      <textarea
        id="idea-redirect"
        value={instruction}
        onChange={(event) => setInstruction(event.target.value)}
        maxLength={600}
        autoFocus
        className={cn(kitBareFieldClass, "mt-2 min-h-20 w-full resize-y p-3 text-[14px]")}
      />
      <div className="mt-2 flex gap-2">
        <Button size="small" isDisabled={!instruction.trim()} onPress={submit}>
          Send the direction
        </Button>
        <Button
          size="small"
          variant="secondary"
          buttonStyle="outline"
          onPress={onCancel}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

/// --------------------------------------------------------------- the card

type CardProps = {
  slot: CalendarPlanSlot;
  idea: CalendarSlotIdea;
  index: number;
  /** How many ideas the post has: the rewriting note names the others. */
  count: number;
  actions?: EditorialConceptActions;
  clientName?: string | null;
  /** Select, then Pick: the page's header picks the selected idea. */
  selected?: boolean;
  onSelect?: () => void;
  /** Roving focus: the one card in the radio group that takes Tab. */
  focusable?: boolean;
  /** Arrow keys move the selection to the previous (-1) or next (1) card. */
  onArrow?: (step: -1 | 1 | "first" | "last") => void;
  /** Enter on the selected card picks it. */
  onPick?: () => void;
  /** Where the rewrite came from, when this page started it. */
  reworkFrom?: IdeaReworkSource;
};

/** "Ideas 1 and 2" for the ideas that are not this one. */
function otherIdeas(index: number, count: number): string {
  const others = Array.from({ length: count }, (_, i) => i + 1).filter((n) => n !== index + 1);
  if (others.length === 0) return "";
  if (others.length === 1) return `Idea ${others[0]}`;
  return `Ideas ${others.slice(0, -1).join(", ")} and ${others[others.length - 1]}`;
}

/** The note under "Rewriting idea 3": where the rewrite came from, and what stays. */
function rewritingNote(index: number, count: number, from: IdeaReworkSource | undefined): string {
  const others = otherIdeas(index, count);
  const stay = others ? `${others} ${count - 1 === 1 ? "stays" : "stay"} as ${count - 1 === 1 ? "it is" : "they are"}.` : "";
  const head =
    from === "chat"
      ? "From your direction in the chat."
      : from === "own"
        ? "From your words."
        : from === "angle"
          ? "A new angle on the same post."
          : "";
  return [head, stay].filter(Boolean).join(" ");
}

/** A skeleton line in the hairline grey, radius 4 (IdeaCard · Rewriting). */
function RewriteLine({ className }: { className?: string }) {
  return <span className={cn("block h-2.5 rounded-[4px] bg-co-line2", className)} />;
}

/** The actions foot: three equal parts on a rule (Redirect, New angle, Edit). */
const FOOT_ACTION =
  "inline-flex h-8 flex-1 items-center justify-center gap-[5px] text-[13px] font-medium text-co-text2 outline-none transition-colors hover:text-co-text focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:cursor-not-allowed disabled:hover:text-co-text2";

/**
 * IdeaCard (library Calendar / Idea card): the media stage, then a body that
 * reads like a pitch with 20 between sections (where it came from and the
 * title, the concept, how it delivers on the objective, evidence, suggested
 * hooks, specs and channels), then Redirect, New angle and Edit on a rule. Solid
 * surface at radius 12, no neutral hairline; selected takes the 2px accent
 * outline and the check on its media. While the agent rewrites it, it becomes
 * IdeaCard · Rewriting: an accent edge, the clock, skeleton lines and the
 * version being replaced.
 */
function IdeaCard({
  slot,
  idea,
  index,
  count,
  actions,
  clientName,
  selected = false,
  onSelect,
  focusable = false,
  onArrow,
  onPick,
  reworkFrom,
}: CardProps) {
  const [inlineRedirect, setInlineRedirect] = useState(false);
  const working = idea.status === "WORKING";
  const live = !!actions && slot.status === "IDEATION" && !working;
  const outputRatio = displayAspectRatio(slot);
  const duration = mediaTypeHasDuration(slot.mediaType)
    ? formatDuration(idea.durationSeconds)
    : null;
  const label = `Idea ${index + 1}`;

  const redirect = () => {
    if (!actions) return;
    const taken = actions.onRedirect?.(idea.id) ?? false;
    if (!taken) setInlineRedirect(true);
  };

  return (
    <article
      data-idea-card={index}
      role={onSelect ? "radio" : undefined}
      aria-checked={onSelect ? selected : undefined}
      aria-label={`${label}: ${idea.title}`}
      aria-busy={working || undefined}
      tabIndex={onSelect ? (focusable ? 0 : -1) : undefined}
      onKeyDown={(event) => {
        // Only the card itself: its own controls keep their keys.
        if (!onSelect || event.target !== event.currentTarget) return;
        if (event.key === "Enter" && selected && onPick) {
          // Already selected: Enter picks, through the page's one pick path.
          event.preventDefault();
          onPick();
        } else if (event.key === " " || event.key === "Enter") {
          event.preventDefault();
          onSelect();
        } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
          event.preventDefault();
          onArrow?.(1);
        } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
          event.preventDefault();
          onArrow?.(-1);
        } else if (event.key === "Home") {
          event.preventDefault();
          onArrow?.("first");
        } else if (event.key === "End") {
          event.preventDefault();
          onArrow?.("last");
        }
      }}
      onClick={(event) => {
        // A click anywhere on the card selects it; its own controls keep their jobs.
        if (
          !onSelect ||
          (event.target as HTMLElement).closest(
            "button, a, input, textarea, select, video, [role='button']",
          )
        )
          return;
        onSelect();
      }}
      className={cn(
        // One column that can shrink: a long evidence line or chip must not
        // widen the card past its track and run under the next card.
        "relative flex min-w-0 snap-start flex-col rounded-card bg-co-surface text-co-text",
        onSelect &&
          "cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-co-accent",
      )}
    >
      {/* The accent edge sits over the media: selected 2px, rewriting 1px. */}
      {selected || working ? (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-0 z-[1] rounded-card",
            selected ? "shadow-[inset_0_0_0_2px_var(--co-accent)]" : "shadow-[inset_0_0_0_1px_var(--co-accent-border)]",
          )}
        />
      ) : null}
      {working ? (
        <div className="h-[214px] shrink-0 rounded-t-card bg-co-recess shadow-[inset_0_-1px_0_var(--co-line)]" aria-hidden="true" />
      ) : (
        <IdeaMediaStage
          idea={idea}
          outputRatio={outputRatio}
          selected={selected}
          onChangeMedia={live ? () => actions?.onChangeMedia(idea.id) : undefined}
        />
      )}

      {working ? (
        <div className="flex flex-1 flex-col gap-5 px-[18px] pb-5 pt-[18px]">
          <div className="flex flex-col gap-1.5">
            <p role="status" className="inline-flex items-center gap-[7px] text-[12px] leading-4 text-co-accent-ink">
              <span className="size-1.5 rounded-full bg-co-accent" aria-hidden="true" />
              <span>
                Rewriting idea {index + 1}
                <ElapsedSince since={idea.updatedAt} suffix="" />
              </span>
            </p>
            <p className="text-[12.5px] leading-[1.45] text-co-text2">{rewritingNote(index, count, reworkFrom)}</p>
            <RewriteLine className="h-3.5 w-[190px] max-w-full" />
          </div>
          <div className="flex flex-col gap-[9px]" aria-hidden="true">
            <RewriteLine />
            <RewriteLine />
            <RewriteLine className="w-[150px]" />
          </div>
          <div className="flex flex-col gap-[9px]" aria-hidden="true">
            <p className={SECTION}>How it delivers on the objective</p>
            <RewriteLine />
            <RewriteLine />
            <RewriteLine className="w-[120px]" />
          </div>
          <div className="flex flex-col gap-[9px]" aria-hidden="true">
            <p className={SECTION}>Evidence</p>
            <span className="block h-8 rounded-[4px] bg-co-recess" />
          </div>
          <div className="flex flex-col gap-[9px]" aria-hidden="true">
            <p className={SECTION}>Suggested hooks</p>
            <span className="flex items-center gap-2.5">
              <span className="size-4 shrink-0 rounded-full border-[1.5px] border-co-line2" />
              <RewriteLine className="flex-1" />
            </span>
          </div>
          <div className="flex-1" />
          <div className="flex flex-col gap-1 pt-3 opacity-55 shadow-[inset_0_1px_0_var(--co-line)]">
            <p className="text-[11.5px] leading-4 text-co-muted">The version being replaced</p>
            <p className="text-[13px] leading-[1.4] text-co-text2">{idea.title}</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col gap-5 px-[18px] pb-5 pt-[18px]">
          <div className="flex flex-col gap-1.5">
            <p className="text-[12px] leading-4 text-co-muted">{ideaSourceLine(idea, index)}</p>
            <h3 className="font-section text-[19px]/[1.12] font-bold text-co-text">{idea.title}</h3>
            <RecommendationLine text={null} />
          </div>

          {idea.synopsis ? <p className="text-[14.5px] leading-[1.5] text-co-text">{idea.synopsis}</p> : null}

          {idea.objectiveClaim ? (
            <div className="flex flex-col gap-1.5">
              <p className={SECTION}>How it delivers on the objective</p>
              <p className="text-[13px] leading-[1.45] text-co-text">{idea.objectiveClaim}</p>
            </div>
          ) : null}

          <EvidenceSection sources={idea.sources ?? []} />

          <HooksSection hooks={idea.hooks ?? []} clientName={clientName} />

          <div className="flex flex-col gap-2.5">
            <p className="text-[12.5px] leading-4 text-co-muted">
              {specsLine({
                pillar: slot.pillarName,
                format: `${MEDIA_TYPE_LABEL[slot.mediaType]} ${outputRatio}`,
                length: duration,
              })}
            </p>
            <ChannelsSection
              idea={idea}
              editable={live}
              onChange={actions ? (channels) => actions.onEditIdea(idea.id, { channels }) : undefined}
            />
          </div>
        </div>
      )}

      {inlineRedirect && actions ? (
        <InlineRedirect
          onCancel={() => setInlineRedirect(false)}
          onSubmit={(instruction) => {
            actions.onModify(idea.id, instruction);
            setInlineRedirect(false);
          }}
        />
      ) : null}
      {actions ? (
        <div
          className={cn(
            "flex items-center px-[18px] pb-4 pt-3 shadow-[inset_0_1px_0_var(--co-line)]",
            working && "opacity-40",
          )}
        >
          <button type="button" disabled={!live} className={FOOT_ACTION} onClick={redirect}>
            <CornerUpRight className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            Redirect
          </button>
          <button type="button" disabled={!live} className={FOOT_ACTION} onClick={() => actions.onRegenerate(idea.id)}>
            <RefreshCw className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
            New angle
          </button>
          {actions.onEditWords ? (
            <button type="button" disabled={!live} className={FOOT_ACTION} onClick={() => actions.onEditWords?.(idea.id)}>
              <Pencil className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
              Edit
            </button>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

// ------------------------------------------------------------- skeletons

/** IdeaCard · Writing: bars on the solid step, a rule, on the surface with a line2 edge. */
function WritingCard() {
  return (
    <div className="flex min-h-[460px] flex-col gap-3.5 rounded-card bg-co-surface p-[22px]">
      <span className="co-idea-bar h-[22px] w-full" />
      <span className="co-idea-bar h-[22px] w-[200px] max-w-full" />
      <span className="co-idea-bar h-3 w-full" />
      <span className="co-idea-bar h-3 w-full" />
      <span className="co-idea-bar h-3 w-full" />
      <span className="co-idea-bar h-3 w-[160px] max-w-full" />
      <span className="h-px w-full bg-co-line" />
      <span className="co-idea-bar h-3 w-full" />
      <span className="co-idea-bar h-3 w-full" />
    </div>
  );
}

/**
 * Ideas being written: three IdeaCard · Writing, no fake progress. With
 * `writing`, one centred Progress card sits over them: the spinner, the time so
 * far, and that the person can leave.
 */
export function EditorialConceptSkeletons({
  viewport = "desktop",
  writing,
}: {
  viewport?: Viewport;
  /** When the run started: the slot's GENERATING_IDEAS claim. */
  writing?: { since: string | null };
}) {
  const cards = (
    <div
      aria-hidden="true"
      className={cn(
        "grid gap-5",
        viewport === "phone"
          ? "grid-cols-1"
          : viewport === "tablet"
            ? "grid-flow-col auto-cols-[340px] overflow-x-auto"
            : "grid-cols-3",
      )}
    >
      {(viewport === "phone" ? [0] : [0, 1, 2]).map((index) => (
        <WritingCard key={index} />
      ))}
    </div>
  );
  if (!writing) return cards;
  return (
    // The cards run to the foot of the page, as the Pen's fill their column.
    <div className="relative flex min-h-[calc(100dvh-240px)] flex-col [&>div:first-child]:flex-1">
      {cards}
      <div className="absolute inset-x-0 top-[302px] flex justify-center px-4">
        <div
          role="status"
          className="flex w-full max-w-[420px] flex-col items-center gap-2 rounded-card bg-co-surface-solid px-7 py-[22px] text-center"
        >
          <LoaderCircle
            className="size-5 animate-spin text-co-muted motion-reduce:animate-none"
            strokeWidth={1.8}
            aria-hidden="true"
          />
          <p className="text-[15px] font-semibold leading-snug text-co-text">
            Writing three ideas
            <ElapsedSince since={writing.since} />
          </p>
          <p className="text-[13px] leading-[1.5] text-co-text2">
            You can leave this page. The post’s card says when they are ready, and so does Home.
          </p>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ phone

/** The full card in a large sheet: hooks, objective and evidence, then Pick. */
function IdeaSheet({
  slot,
  idea,
  index,
  count,
  clientName,
  actions,
  onPick,
  onClose,
  redirect,
}: {
  slot: CalendarPlanSlot;
  idea: CalendarSlotIdea;
  index: number;
  count: number;
  clientName?: string | null;
  actions?: EditorialConceptActions;
  onPick?: () => void;
  onClose: () => void;
  /** Redirect with no chat rail: the sheet opens on the direction box. */
  redirect?: (instruction: string) => void;
}) {
  const outputRatio = displayAspectRatio(slot);
  const duration = mediaTypeHasDuration(slot.mediaType)
    ? formatDuration(idea.durationSeconds)
    : null;
  const live =
    !!actions && slot.status === "IDEATION" && idea.status !== "WORKING";
  const source = ideaSourceLine(idea, index).replace(
    /^Idea \d+/,
    `Idea ${index + 1} of ${count}`,
  );
  return (
    <KitModal
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      aria-label={`Idea ${index + 1}: hooks, objective and evidence`}
      hideCloseButton
      overlayClassName="co-post-sheet-overlay"
      modalClassName="co-post-sheet max-h-[calc(100dvh-16px)]"
    >
      <span className="co-post-sheet-grabber" aria-hidden="true" />
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-4 pb-2">
        <div className="flex items-start justify-between gap-3 pt-1">
          <p className="pt-2 text-[12.5px] text-co-muted">{source}</p>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="co-glass-control grid size-9 shrink-0 place-items-center rounded-pill text-co-muted outline-none hover:text-co-text focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <X className="size-4" strokeWidth={1.8} aria-hidden="true" />
          </button>
        </div>
        <h3 className="mt-2 font-section text-[22px] font-bold leading-[1.15] text-co-text">
          {idea.title}
        </h3>
        {redirect ? (
          <div className="-mx-4 mt-4">
            <InlineRedirect onSubmit={redirect} onCancel={onClose} />
          </div>
        ) : null}
        <div className="mt-6">
          <HooksSection
            hooks={idea.hooks ?? []}
            clientName={clientName}
            alwaysOpen
          />
        </div>
        {idea.objectiveClaim ? (
          <div className="mt-6">
            <p className={SECTION}>How it delivers on the objective</p>
            <p className="mt-2.5 text-[15px] leading-[1.5] text-co-text2">
              {idea.objectiveClaim}
            </p>
          </div>
        ) : null}
        <div className="mt-6">
          <EvidenceSection sources={idea.sources ?? []} />
        </div>
        <p className="mt-5 text-[12.5px] text-co-muted">
          {specsLine({
            pillar: slot.pillarName,
            format: `${MEDIA_TYPE_LABEL[slot.mediaType]} ${outputRatio}`,
            length: duration,
          })}
        </p>
        <div className="mt-4">
          <ChannelsSection
            idea={idea}
            editable={live}
            onChange={
              actions
                ? (channels) => actions.onEditIdea(idea.id, { channels })
                : undefined
            }
          />
        </div>
      </div>
      {onPick ? (
        <div className="border-t border-co-line px-4 pt-3">
          <Button
            className="h-11 w-full text-[14px]"
            icon={Check}
            iconPosition="right"
            iconClassName="size-3.5"
            onPress={onPick}
          >
            Pick idea {index + 1}
          </Button>
        </div>
      ) : null}
    </KitModal>
  );
}

/** What the phone's story view says at its top (Bar / Story · Phone). */
export interface PhoneStoryInfo {
  /** The post's title. */
  title: string;
  /** "Wed 10 Mar". */
  dateLabel: string;
  onBack: () => void;
}

const MEDIA_GLASS =
  "grid shrink-0 place-items-center rounded-pill bg-[var(--co-media-glass,rgba(18,18,18,0.4))] text-[var(--co-media-ink,#fafafa)] shadow-[0_4px_12px_var(--co-shell-shadow)] outline-none backdrop-blur-[30px] focus-visible:outline-2 focus-visible:outline-co-accent";

/**
 * The phone (Pen: Phone · pick an idea, one per screen): each idea is a full
 * screen of its own, its reference behind it under a shade, the story bar's
 * segments on top, the idea's words over the foot, and the action bar (⋯ and
 * Pick idea) floating at the bottom. A sideways swipe moves between ideas; the
 * idea on screen is the selection. Hooks, objective and evidence open the
 * full card in a sheet.
 */
function PhoneIdeaBoard({
  slot,
  ideas,
  actions,
  clientName,
  selectedIdeaId,
  onSelectIdea,
  onPickIdea,
  readOnly,
  story,
}: {
  slot: CalendarPlanSlot;
  ideas: CalendarSlotIdea[];
  actions?: EditorialConceptActions;
  clientName?: string | null;
  selectedIdeaId: string | null;
  onSelectIdea?: (ideaId: string) => void;
  onPickIdea?: (ideaId: string) => void;
  readOnly?: boolean;
  story?: PhoneStoryInfo;
}) {
  const portal = useCoShellPortal();
  const trackRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(0);
  const [sheetFor, setSheetFor] = useState<string | null>(null);
  // Redirect with no chat to take it: the direction is typed in the sheet.
  const [sheetRedirect, setSheetRedirect] = useState(false);
  const selectedIndex = ideas.findIndex((idea) => idea.id === selectedIdeaId);

  // The idea on screen is the selection: select the first one on arrival.
  useEffect(() => {
    if (!onSelectIdea || ideas.length === 0 || selectedIndex >= 0) return;
    onSelectIdea(ideas[0]!.id);
  }, [onSelectIdea, ideas, selectedIndex]);

  // A selection made elsewhere brings its idea on screen.
  useEffect(() => {
    const track = trackRef.current;
    if (!track || selectedIndex < 0 || selectedIndex === onScreen) return;
    track.scrollTo({ left: selectedIndex * track.clientWidth, behavior: "auto" });
    // Only the selection drives this; the scroll handler owns onScreen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex]);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const next = Math.round(track.scrollLeft / track.clientWidth);
    if (next === onScreen || !ideas[next]) return;
    setOnScreen(next);
    onSelectIdea?.(ideas[next]!.id);
  };

  const current = ideas[onScreen] ?? null;
  const currentLive = !!actions && !!current && slot.status === "IDEATION" && current.status !== "WORKING";
  const sheetIndex = ideas.findIndex((idea) => idea.id === sheetFor);
  const sheetIdea = sheetIndex >= 0 ? ideas[sheetIndex] : null;
  const redirect = (idea: CalendarSlotIdea) => {
    const taken = actions?.onRedirect?.(idea.id) ?? false;
    if (taken) return;
    setSheetRedirect(true);
    setSheetFor(idea.id);
  };

  const ideaMenu = current && actions ? (
    <KitMenuTrigger>
      <KitPressable>
        <button type="button" aria-label="More for this idea" className="co-glass-chrome grid size-12 shrink-0 place-items-center rounded-pill text-co-text2 outline-none focus-visible:outline-2 focus-visible:outline-co-accent">
          <MoreHorizontal className="size-5" aria-hidden="true" />
        </button>
      </KitPressable>
      <KitMenuPopover placement="top start">
        <KitMenu
          aria-label="Idea actions"
          disabledKeys={currentLive ? [] : ["redirect", "angle", "edit", "media"]}
          onAction={(key) => {
            if (key === "redirect") redirect(current);
            else if (key === "angle") actions.onRegenerate(current.id);
            else if (key === "edit") actions.onEditWords?.(current.id);
            else if (key === "media") actions.onChangeMedia(current.id);
            else if (key === "own") actions.onWriteOwn?.();
          }}
        >
          <KitMenuItem id="redirect" icon={CornerUpRight}>Redirect</KitMenuItem>
          <KitMenuItem id="angle" icon={RefreshCw}>New angle</KitMenuItem>
          {actions.onEditWords ? <KitMenuItem id="edit" icon={PenLine}>Edit</KitMenuItem> : null}
          <KitMenuItem id="media" icon={ImagePlus}>{current.reference ? "Change media" : "Add media"}</KitMenuItem>
          {actions.onWriteOwn ? <KitMenuItem id="own" icon={Pencil}>Write yours</KitMenuItem> : null}
        </KitMenu>
      </KitMenuPopover>
    </KitMenuTrigger>
  ) : null;

  const screen = (
    <div
      className={cn(
        "bg-[#121212] text-white",
        // Full screen over the shell; read-only (a missed day) it sits in the page.
        story ? "fixed inset-0 z-[var(--z-index-modal)]" : "relative h-[78dvh] overflow-hidden rounded-card",
        readOnly && "opacity-60",
      )}
      aria-disabled={readOnly || undefined}
    >
      <div
        ref={trackRef}
        onScroll={onScroll}
        className="co-idea-swipe flex h-full snap-x snap-mandatory overflow-x-auto"
        role="radiogroup"
        aria-label="Ideas"
      >
        {ideas.map((idea, index) => {
          const working = idea.status === "WORKING";
          const live = !!actions && slot.status === "IDEATION" && !working;
          const hook = foldHooks(idea.hooks ?? [], false).shown[0] ?? null;
          const reference = idea.reference;
          const gone = referenceGone(reference);
          const media = reference && !gone;
          return (
            <section
              key={idea.id}
              role="radio"
              aria-checked={idea.id === selectedIdeaId}
              aria-label={`Idea ${index + 1}: ${idea.title}`}
              className="relative h-full w-full shrink-0 snap-center overflow-hidden"
            >
              {media ? (
                <div className="absolute inset-0">
                  <ReferenceMedia reference={reference!} fill />
                  {/* Idea media · Phone's shade: clear at the top third, near black at the foot. */}
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 bottom-0 h-[67%] bg-[linear-gradient(180deg,#00000000_0%,#0000009e_30%,#000000f2_100%)]"
                  />
                </div>
              ) : null}
              <div
                className={cn(
                  "absolute inset-x-4 flex flex-col gap-2.5",
                  media ? "bottom-[100px]" : "top-[176px]",
                  working && "opacity-60",
                )}
              >
                {working ? (
                  <p className="inline-flex items-center gap-2 text-[12.5px] font-semibold text-co-accent" role="status">
                    <span className="size-1.5 rounded-full bg-co-accent" aria-hidden="true" />
                    Rewriting
                    <ElapsedSince since={idea.updatedAt} suffix="" />
                  </p>
                ) : null}
                <h3 className={cn("font-section font-bold text-white", media ? "line-clamp-2 text-[26px] !leading-[28.6px]" : "text-[34px] !leading-[37.4px]")}>
                  {idea.title}
                </h3>
                <p className={cn("text-[17px] leading-[1.45] text-white", media && "line-clamp-4")}>{idea.synopsis}</p>
                {media ? (
                  <p className="text-[12px] text-white/70">{ideaSourceLine(idea, index).replace(/^Idea \d+ · /, "")}</p>
                ) : (
                  <div className="mt-1 flex items-center justify-between gap-3 border-t border-white/10 py-3 text-[13px] text-white/70">
                    <span>{gone ? "This reference is no longer available." : "No reference. The idea stands on its words."}</span>
                    {live ? (
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap font-semibold text-co-accent outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
                        onClick={() => actions?.onChangeMedia(idea.id)}
                      >
                        <Plus className="size-3.5" strokeWidth={1.8} aria-hidden="true" />
                        {gone ? "Replace it" : "Add one"}
                      </button>
                    ) : null}
                  </div>
                )}
                {hook ? (
                  <div className="flex flex-col gap-[5px] pt-0.5">
                    <p className="font-section text-[12.5px] font-bold tracking-[0.8px] text-white/70">
                      {(clientName?.trim() || "The client") + " sees this hook"}
                    </p>
                    <p className="text-[15px] leading-[1.4] text-white">“{hook}”</p>
                  </div>
                ) : null}
                <button
                  type="button"
                  className="inline-flex items-center gap-1.5 self-start pt-1 text-[14px] font-semibold text-white outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
                  onClick={() => {
                    setSheetRedirect(false);
                    setSheetFor(idea.id);
                  }}
                >
                  Hooks, objective and evidence
                  <ChevronUp className="size-4" strokeWidth={1.8} aria-hidden="true" />
                </button>
              </div>
            </section>
          );
        })}
      </div>

      {/* Bar / Story · Phone: a shade under the segments and the media top lane. */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[200px] bg-[linear-gradient(180deg,#000000b3_0%,#00000000_100%)]" />
      <div className="absolute inset-x-3 top-2.5 flex gap-1" role="tablist" aria-label="Ideas">
        {ideas.map((idea, index) => (
          <button
            key={idea.id}
            type="button"
            role="tab"
            aria-selected={index === onScreen}
            aria-label={`Idea ${index + 1}`}
            onClick={() => trackRef.current?.scrollTo({ left: index * trackRef.current.clientWidth, behavior: "smooth" })}
            className="flex h-3 flex-1 items-start outline-none focus-visible:outline-2 focus-visible:outline-co-accent"
          >
            <span
              className={cn(
                "h-[3px] w-full",
                index < onScreen ? "bg-white" : index === onScreen ? "bg-co-accent" : "bg-white/30",
              )}
            />
          </button>
        ))}
      </div>
      <div className="absolute inset-x-1 top-5 flex items-center gap-0.5">
        {story ? (
          <button type="button" aria-label="Back" onClick={story.onBack} className={cn(MEDIA_GLASS, "h-11 w-10")}>
            <ChevronLeft className="size-[22px]" aria-hidden="true" />
          </button>
        ) : null}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5 px-1">
          <span className="line-clamp-2 text-[14px] font-semibold leading-[1.3] text-white">{story?.title ?? slot.title}</span>
          <span className="text-[12px] text-white/70">
            {story ? `${story.dateLabel} · ` : ""}idea {onScreen + 1} of {ideas.length}
          </span>
        </span>
      </div>

      {/* Bar / Action · Phone: ⋯ and the one primary, floating. */}
      <div className="absolute inset-x-4 bottom-4 flex items-center gap-2 p-2">
        {ideaMenu}
        {onPickIdea && current ? (
          <button
            type="button"
            disabled={readOnly || current.status === "WORKING"}
            onClick={() => onPickIdea(current.id)}
            className="inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-pill bg-co-accent text-[16px] font-semibold text-co-on-accent shadow-[0_8px_24px_var(--co-shell-shadow)] outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
          >
            Pick idea
            <Check className="size-[18px]" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      {sheetIdea ? (
        <IdeaSheet
          slot={slot}
          idea={sheetIdea}
          index={sheetIndex}
          count={ideas.length}
          clientName={clientName}
          actions={actions}
          onClose={() => setSheetFor(null)}
          redirect={
            sheetRedirect && actions
              ? (instruction) => {
                  actions.onModify(sheetIdea.id, instruction);
                  setSheetFor(null);
                }
              : undefined
          }
          onPick={
            onPickIdea && !readOnly && sheetIdea.status !== "WORKING"
              ? () => {
                  setSheetFor(null);
                  onPickIdea(sheetIdea.id);
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );

  return story && portal ? createPortal(screen, portal) : screen;
}

// ------------------------------------------------------------------ board

export default function EditorialConceptBoard({
  phoneStory,
  slot,
  actions,
  selectedIdeaId = null,
  onSelectIdea,
  onPickIdea,
  viewport = "desktop",
  clientName,
  readOnly = false,
  footer,
  reworkFrom,
}: {
  /** The phone's story view: the post's title, its day and the way back. */
  phoneStory?: PhoneStoryInfo;
  /** Where each rewrite this page started came from (the rewriting note). */
  reworkFrom?: Readonly<Record<string, IdeaReworkSource>>;
  slot: CalendarPlanSlot;
  actions?: EditorialConceptActions;
  selectedIdeaId?: string | null;
  onSelectIdea?: (ideaId: string) => void;
  /** Select and pick in one go: the phone sheet's "Pick idea 2". */
  onPickIdea?: (ideaId: string) => void;
  viewport?: Viewport;
  clientName?: string | null;
  /** The day passed: the three ideas stay readable, dimmed, with no controls. */
  readOnly?: boolean;
  footer?: ReactNode;
}) {
  const ideas = [...(slot.ideas ?? [])].sort((a, b) => a.order - b.order);
  const reworking = ideas.some((idea) => idea.status === "WORKING");
  const groupRef = useRef<HTMLDivElement>(null);
  const selectedIndex = ideas.findIndex((idea) => idea.id === selectedIdeaId);
  const boardActions = readOnly ? undefined : actions;
  const select = readOnly ? undefined : onSelectIdea;

  // Radio-group arrows: move the selection and the focus together.
  const moveSelection = (from: number, step: -1 | 1 | "first" | "last") => {
    if (!select || ideas.length === 0) return;
    const next =
      step === "first"
        ? 0
        : step === "last"
          ? ideas.length - 1
          : (from + step + ideas.length) % ideas.length;
    const idea = ideas[next];
    if (!idea) return;
    select(idea.id);
    groupRef.current
      ?.querySelector<HTMLElement>(`[data-idea-card="${next}"]`)
      ?.focus();
  };

  const extras =
    boardActions && (boardActions.onWriteOwn || boardActions.onRepitchAll) ? (
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-1">
        {boardActions.onWriteOwn ? (
          <button
            type="button"
            className={TEXT_BUTTON}
            disabled={reworking}
            onClick={boardActions.onWriteOwn}
          >
            <PenLine
              className="size-3.5"
              strokeWidth={1.8}
              aria-hidden="true"
            />
            Write your own idea
          </button>
        ) : null}
        <button
          type="button"
          className={TEXT_BUTTON}
          disabled={reworking}
          onClick={boardActions.onRepitchAll}
        >
          <RefreshCw
            className="size-3.5"
            strokeWidth={1.8}
            aria-hidden="true"
          />
          Re-pitch all
        </button>
      </div>
    ) : null;

  if (viewport === "phone") {
    return (
      <div>
        <PhoneIdeaBoard
          story={readOnly ? undefined : phoneStory}
          slot={slot}
          ideas={ideas}
          actions={boardActions}
          clientName={clientName}
          selectedIdeaId={selectedIdeaId}
          onSelectIdea={select}
          onPickIdea={readOnly ? undefined : onPickIdea}
          readOnly={readOnly}
        />
        {extras}
        {footer}
      </div>
    );
  }

  return (
    // The cards run to the foot of the page, as the Pen's fill their column.
    <div className={cn("flex flex-col", viewport !== "tablet" && "min-h-[calc(100dvh-240px)]")}>
      <div
        ref={groupRef}
        role={select ? "radiogroup" : undefined}
        aria-label={select ? "Ideas" : undefined}
        aria-disabled={readOnly || undefined}
        className={cn(
          "grid flex-1 grid-flow-col overflow-x-auto overscroll-x-contain",
          viewport === "tablet"
            ? "-mx-6 snap-x snap-mandatory scroll-px-6 auto-cols-[340px] gap-3.5 px-6 pb-5"
            : "snap-x auto-cols-[minmax(300px,1fr)] gap-4",
          readOnly && "opacity-60",
        )}
      >
        {ideas.map((idea, index) => (
          <IdeaCard
            key={idea.id}
            slot={slot}
            idea={idea}
            index={index}
            count={ideas.length}
            reworkFrom={reworkFrom?.[idea.id]}
            actions={boardActions}
            clientName={clientName}
            selected={idea.id === selectedIdeaId}
            focusable={
              idea.id === (selectedIndex >= 0 ? selectedIdeaId : ideas[0]?.id)
            }
            onArrow={select ? (step) => moveSelection(index, step) : undefined}
            onSelect={select ? () => select(idea.id) : undefined}
            onPick={
              onPickIdea && !readOnly && idea.status !== "WORKING"
                ? () => onPickIdea(idea.id)
                : undefined
            }
          />
        ))}
      </div>
      {select ? (
        <p role="status" aria-live="polite" className="sr-only">
          {selectedIndex >= 0 ? `Idea ${selectedIndex + 1} selected` : ""}
        </p>
      ) : null}
      {footer}
    </div>
  );
}

export type { Viewport as IdeaBoardViewport };
